import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { Button, Flex, H2, Input, SelectDropdown, Span, ToolTip } from '../../lib/components'
import { Pagination } from '../../lib/components/Pagination'
import { SearchParams } from '../../lib/types'
import { SM } from '../../lib/utils/constants'
import { getTimestampWithTimezone, trimText } from '../../lib/utils/helpers'
import { useBreakpoint } from '../../lib/utils/hooks'
import { businessInquiriesSort } from '../../lib/utils/message'
import { queriesServices } from '../../services'
import '../queries-list/queries-list.scss'

interface BusinessContact {
  id: number
  name: string
  email: string
  company: string
  message: string
  created_at: string
}

const BusinessInquiriesList = () => {
  const { breakpoint } = useBreakpoint()
  const [contacts, setContacts] = useState<BusinessContact[]>()
  const [searchParams, setSearchParams] = useState<SearchParams>({
    limit: '10',
    offset: '0',
    filterBy: '',
    sortBy: '',
    keyword: '',
  })
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    count: 5,
    offset: 0,
  })
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    fetchBusinessInquiriesList()
  }, [currentPage, searchParams])

  const fetchBusinessInquiriesList = async () => {
    const response: any = await queriesServices.searchBusinessContacts(searchParams)
    if (response && response.data && response.status === 200) {
      setContacts(response.data)
      setPagination(response.pagination)
    }
  }

  const handleSearchParams = async ({ name, value }: { name: string; value: string }) => {
    const { limit } = pagination
    setSearchParams({
      ...searchParams,
      offset: '0',
      limit: limit.toString(),
      [name]: value,
    })
    setCurrentPage(1)
    setPagination({ ...pagination, offset: 0 })
  }

  const resetSearchParams = () => {
    setSearchParams({
      limit: '10',
      offset: '0',
      keyword: '',
      filterBy: '',
      sortBy: '',
    })
  }

  const handleCurrentPage = (currentPage: number) => {
    const { limit } = pagination
    setCurrentPage(currentPage)
    setSearchParams({
      ...searchParams,
      offset: `${(currentPage - 1) * limit}`,
    })
    setPagination({ ...pagination, offset: (currentPage - 1) * limit })
  }

  return (
    <Flex variant='column-center'>
      <H2 classes={['mb-48']} text='Business Inquiries' />
      <div className='inquiries-toolbar-layout gap-12 mb-16 mt-16'>
        <Input
          name='keyword'
          value={searchParams.keyword || ''}
          handleChange={(e) => handleSearchParams({ name: e.target.name, value: e.target.value })}
          placeholder='Search'
        />
        <div className='sort-reset'>
          <SelectDropdown
            name='sortBy'
            id='sortById'
            formId=''
            list={businessInquiriesSort}
            value={searchParams.sortBy}
            placeholder={'Sort'}
            handleChange={(val) => handleSearchParams({ name: 'sortBy', value: val })}
            dataTestId='business-inquiries-sort-list-dropdown'
          />
          <Button
            handleClick={() => resetSearchParams()}
            classes={['reset-btn']}
            variant='secondary-btn'
            text='Reset'
          />
        </div>
      </div>
      {contacts && contacts.length ? (
        <div className='w-100'>
          <Flex
            variant='row'
            classes={['mb-24', 'table_scroll_horizontal']}
            dataTestId='business-inquiries-table-container'
          >
            <table className='styled-table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Email</th>
                  <th>Message</th>
                  <th>Received on</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => {
                  return (
                    <tr key={uuidv4()}>
                      <td>{contact.name}</td>
                      <td>{contact.company}</td>
                      <td>{contact.email}</td>
                      <td>
                        {
                          <ToolTip content={contact.message}>
                            <span>{trimText(contact.message)}</span>
                          </ToolTip>
                        }
                      </td>
                      <td>
                        {getTimestampWithTimezone(
                          new Date(contact.created_at),
                          'Do MMM YYYY HH:mm:ss zzz',
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </Flex>
          {breakpoint && breakpoint === SM
            ? null
            : contacts &&
              contacts.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  handleCurrentPage={handleCurrentPage}
                  pagination={pagination}
                />
              )}
        </div>
      ) : (
        <Flex variant={'column-center'} classes={['mt-12']}>
          <Span classes={['center']} variant={'thin'} text='No Business Inquiries Found' />
        </Flex>
      )}
    </Flex>
  )
}

export { BusinessInquiriesList }
